import React, { useEffect, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { useSelector } from 'react-redux';
import { RootState } from '../../reduxToolkit/store';
import { useNavigate } from 'react-router-dom';
import { propertyTypeService } from '../../service/propertyType.service';
import Swal from 'sweetalert2';
import * as Icon from 'react-bootstrap-icons';
import { Button } from 'react-bootstrap';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';

import { PropertyType } from '../../models/propertyType.model';

interface FormData {
    id?: number;
    name: string;
    description: string;
    isActive: boolean;
}

const Type: React.FC = () => {
    const [types, setTypes] = useState<PropertyType[]>([]);
    const [visible, setVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedType, setSelectedType] = useState<PropertyType | null>(null);
    const [loading, setLoading] = useState(false);
    const [listLoading, setListLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const navigate = useNavigate();
    
    // Table state with localStorage persistence
    const [page, setPage] = useState(() => {
        const savedPage = localStorage.getItem('typeTablePage');
        return savedPage ? parseInt(savedPage) : 1;
    });
    const [rows, setRows] = useState(() => {
        const savedRows = localStorage.getItem('typeTableRows');
        return savedRows ? parseInt(savedRows) : 10;
    });
    const [totalRecords, setTotalRecords] = useState(0);
    const [globalFilter, setGlobalFilter] = useState("");
    const [sortField, setSortField] = useState(() => {
        const savedSort = localStorage.getItem('typeTableSortField');
        return savedSort || "name";
    });
    const [sortOrder, setSortOrder] = useState<1 | -1>(() => {
        const savedOrder = localStorage.getItem('typeTableSortOrder');
        return (savedOrder ? parseInt(savedOrder) : 1) as 1 | -1;
    });

    const [form, setForm] = useState<FormData>({
        name: '',
        description: '',
        isActive: true
    });

    const userRole = useSelector((state: RootState) => state.auth.userRole) ?? '';
    const isReadOnly = userRole !== 'Administrator';

    useEffect(() => {
        const fetchTypes = async () => {
            setListLoading(true);
            try {
                // Use getAll for now and implement client-side pagination
                const data = await propertyTypeService.getAll();
                const filteredData = globalFilter
                    ? data.filter((item: PropertyType) =>
                          Object.values(item)
                              .join(' ')
                              .toLowerCase()
                              .includes(globalFilter.toLowerCase())
                      )
                    : data;

                // Sort the data
                const sortedData = [...filteredData].sort((a: any, b: any) => {
                    const aValue = a[sortField];
                    const bValue = b[sortField];
                    const modifier = sortOrder === 1 ? 1 : -1;

                    if (aValue < bValue) return -1 * modifier;
                    if (aValue > bValue) return 1 * modifier;
                    return 0;
                });

                // Calculate pagination
                const totalItems = sortedData.length;
                const start = (page - 1) * rows;
                const end = start + rows;
                const paginatedData = sortedData.slice(start, end);

                setTypes(paginatedData);
                setTotalRecords(totalItems);
            } catch (error) {
                setTypes([]);
                setTotalRecords(0);
                Swal.fire({
                    toast: true,
                    position: 'bottom',
                    icon: 'error',
                    title: 'Failed to fetch property types',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                    customClass: { popup: 'swal2-toast-bottom' }
                });
            } finally {
                setListLoading(false);
            }
        };
        fetchTypes();
    }, [success, page, rows, sortField, sortOrder, globalFilter]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        let fieldValue: string | boolean = value;

        if (type === 'checkbox') {
            fieldValue = (e.target as HTMLInputElement).checked;
        }

        setForm(prev => ({
            ...prev,
            [name]: fieldValue
        }));
    };

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(false);
        try {
            if (editMode && selectedType) {
                const updated = await propertyTypeService.update(selectedType.id, form);
                setTypes(prev => prev.map(t => (t.id === selectedType.id ? updated : t)));
                Swal.fire({
                    toast: true,
                    position: 'bottom',
                    icon: 'success',
                    title: 'Property type updated successfully',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                    customClass: { popup: 'swal2-toast-bottom' }
                });
            } else {
                const created = await propertyTypeService.create(form);
                setTypes(prev => [...prev, created]);
                Swal.fire({
                    toast: true,
                    position: 'bottom',
                    icon: 'success',
                    title: 'Property type created successfully',
                    showConfirmButton: false,
                    timer: 3000,
                    timerProgressBar: true,
                    customClass: { popup: 'swal2-toast-bottom' }
                });
            }
            setVisible(false);
            setForm({ name: '', description: '', isActive: true });
            setSuccess(true);
        } catch (err: any) {
            setError(err?.response?.data?.message || err.message || "Error saving property type");
            Swal.fire({
                toast: true,
                position: 'bottom',
                icon: 'error',
                title: 'Failed to save property type',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                customClass: { popup: 'swal2-toast-bottom' }
            });
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (type: PropertyType) => {
        setSelectedType(type);
        setForm({
            id: type.id,
            name: type.name,
            description: type.description || '',
            isActive: type.isActive ?? true
        });
        setEditMode(true);
        setVisible(true);
    };

    const handleDelete = async (type: PropertyType) => {
        if (!type.id) return;
        try {
            await propertyTypeService.delete(type.id);
            setTypes(prev => prev.filter(t => t.id !== type.id));
            setSuccess(true);
            Swal.fire({
                toast: true,
                position: 'bottom',
                icon: 'success',
                title: 'Property type deleted successfully',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                customClass: { popup: 'swal2-toast-bottom' }
            });
        } catch {
            setError("Failed to delete property type");
            Swal.fire({
                toast: true,
                position: 'bottom',
                icon: 'error',
                title: 'Failed to delete property type',
                showConfirmButton: false,
                timer: 3000,
                timerProgressBar: true,
                customClass: { popup: 'swal2-toast-bottom' }
            });
        }
    };

    const handleCancel = () => {
        setVisible(false);
        setForm({ name: '', description: '', isActive: true });
        setEditMode(false);
        setSelectedType(null);
        setError(null);
    };

    const actionTemplate = (rowData: PropertyType) => {
        return (
            <div className="d-flex align-items-center gap-2">
                <Button 
                    variant="link" 
                    className="p-0" 
                    onClick={() => handleEdit(rowData)} 
                    title="Edit" 
                    style={{ color: "#198754" }}
                    disabled={isReadOnly}
                >
                    <Icon.PencilSquare size={20} />
                </Button>
                <Button 
                    variant="link" 
                    className="p-0" 
                    onClick={() => handleDelete(rowData)} 
                    title="Delete" 
                    style={{ color: "#dc3545" }}
                    disabled={isReadOnly}
                >
                    <Icon.Trash size={20} />
                </Button>
            </div>
        );
    };

    const openNew = () => {
        setEditMode(false);
        setForm({ name: '', description: '', isActive: true });
        setVisible(true);
        setError(null);
    };

    return (
        <div className="content">
            <div className="content-header">
                <div className="container d-flex align-items-center">
                    <h6 className="_600 mb-2 page-heading">Property Types</h6>
                </div>
            </div>
            <div className="content-body">
                <div className="container mt-4">
                    <div className="row shadow">
                        <div className="col-md-12 mb-3">
                            <div className="d-flex align-items-center gap-2 my-3">
                                <div className="position-relative flex-grow-1">
                                    <InputText
                                        value={globalFilter}
                                        onChange={e => {
                                            setGlobalFilter(e.target.value);
                                            setPage(1);
                                            localStorage.setItem('typeTablePage', '1');
                                        }}
                                        placeholder="Search"
                                        className="w-100"
                                        style={{ paddingLeft: '35px' }}
                                    />
                                    <Icon.Search 
                                        style={{ 
                                            position: 'absolute', 
                                            left: '10px', 
                                            top: '50%', 
                                            transform: 'translateY(-50%)',
                                            color: '#6c757d'
                                        }} 
                                    />
                                </div>
                                {userRole === 'Administrator' && (
                                    <Button 
                                        className="btn btn-primary ms-2" 
                                        style={{ minWidth: 160 }} 
                                        onClick={openNew}
                                    >
                                        <Icon.PlusCircle className="me-1" /> Create Property Type
                                    </Button>
                                )}
                            </div>

                            <DataTable
                                value={types}
                                paginator
                                rows={rows}
                                first={(page - 1) * rows}
                                totalRecords={totalRecords}
                                onPage={(e) => {
                                    const newPage = (e.first ?? 0) / (e.rows ?? 10) + 1;
                                    setPage(newPage);
                                    setRows(e.rows ?? 10);
                                    localStorage.setItem('typeTablePage', newPage.toString());
                                    localStorage.setItem('typeTableRows', (e.rows ?? 10).toString());
                                }}
                                onSort={(e) => {
                                    const newSortField = e.sortField || "name";
                                    const newSortOrder = e.sortOrder as 1 | -1;
                                    setSortField(newSortField);
                                    setSortOrder(newSortOrder);
                                    localStorage.setItem('typeTableSortField', newSortField);
                                    localStorage.setItem('typeTableSortOrder', newSortOrder.toString());
                                }}
                                sortField={sortField}
                                sortOrder={sortOrder}
                                lazy
                                loading={listLoading}
                                dataKey="id"
                                className="grid-table"
                                rowsPerPageOptions={[5, 10, 20]}
                                stripedRows
                                sortMode="single"
                                emptyMessage="No property types found"
                            >
                                <Column field="name" header="Name" sortable />
                                <Column field="description" header="Description" sortable />
                                <Column header="Actions" body={actionTemplate} style={{ minWidth: '120px' }} />
                            </DataTable>
                        </div>
                    </div>
                </div>
            </div>

            {visible && (
                <div className="modal show d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.3)" }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editMode ? "Edit Property Type" : "Create Property Type"}
                                </h5>
                                <button type="button" className="btn-close" onClick={handleCancel}></button>
                            </div>
                            <form onSubmit={onSubmit}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Name:</label>
                                        <input
                                            className="form-control"
                                            name="name"
                                            value={form.name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Description:</label>
                                        <textarea
                                            className="form-control"
                                            name="description"
                                            value={form.description}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    {error && <div className="alert alert-danger">{error}</div>}
                                </div>
                                <div className="modal-footer">
                                    <Button type="submit" variant="primary" disabled={loading}>
                                        {loading ? (editMode ? "Updating..." : "Creating...") : (editMode ? "Update" : "Create")}
                                    </Button>
                                    <Button variant="secondary" onClick={handleCancel}>
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {visible && (
                <div className="modal show d-block" tabIndex={-1} style={{ background: "rgba(0,0,0,0.3)" }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {editMode ? "Edit Property Type" : "Create Property Type"}
                                </h5>
                                <button type="button" className="btn-close" onClick={handleCancel}></button>
                            </div>
                            <form onSubmit={onSubmit}>
                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label className="form-label">Name:</label>
                                        <input
                                            className="form-control"
                                            name="name"
                                            value={form.name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label">Description:</label>
                                        <textarea
                                            className="form-control"
                                            name="description"
                                            value={form.description}
                                            onChange={handleChange}
                                        />
                                    </div>
                                    {error && <div className="alert alert-danger">{error}</div>}
                                </div>
                                <div className="modal-footer">
                                    <Button type="submit" variant="primary" disabled={loading}>
                                        {loading ? (editMode ? "Updating..." : "Creating...") : (editMode ? "Update" : "Create")}
                                    </Button>
                                    <Button variant="secondary" onClick={handleCancel}>
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Type;